define([
	'internal_util/Logger',
	'internal_util/Util',
	'internal_util/Configuration',
	'internal_util/StorageUtil',
	'internal_util/CryptoUtil',
	'internal_util/OptionsUtil',
	'internal_util/X500Util',
	'internal_util/Base64Util',
	'model/Error',
	'model/ErrorType',
	'model/AuthenticationOption',
	'model/AuthenticationOptionGroup',
	'model/CloudIdentity',
	'model/Image',
	'factory/AttributesFactory',
	'service/EventListenerRegistry',
	'service/SessionManager',
	'service/OptionsManager',
	'factory/CloudIdentityFactory',
	'factory/BindingsFactory',
	'amdi18n-loader?!service/nls/CloudIdentitiesManager_strings',
	'amdi18n-loader?!state/nls/AuthenticationOptionSelectionState_strings',
	'lib/jquery',
	'lib/sha1',
	'@ecsec/base-libs/dist/webcrypto',
	'@ecsec/base-libs/dist/util'
], function (
		Logger,
		Util,
		Configuration,
		StorageUtil,
		CryptoUtil,
		OptionsUtil,
		X500Util,
		Base64Util,
		Error,
		ErrorType,
		AuthenticationOption,
		AuthenticationOptionGroup,
		CloudIdentity,
		Image,
		AttributesFactory,
		EventListenerRegistry,
		SessionManager,
		OptionsManager,
		CloudIdentityFactory,
		BindingsFactory,
		Strings,
		OptionStrings,
		jQuery,
		Sha1,
		
		webcrypto,
		ecUtil,
		) {
	'use strict';

	/**
	 * CloudIdentitiesManager class
	 */
	return function () {
		let _identities = new Object(),
				_providerInfo = null,
				_authenticationOptions = null,
				_requestedAttributes = null,
				_authenticationOptionsUi = new Object(),
				_optionsManager = null;


		// private methods

		function log(method, message, data) {
			Logger.log('CloudIdentitiesManager', method, message, data);
		}

		function callLog(method, message, data, event, callbacks) {
			log(method, message, (data instanceof CloudIdentity) ? data.id() : data);
			EventListenerRegistry.callListeners(event, data, function () {
				Util.callCallbacks(callbacks, data);
			});
		}

		function readFromLocalStorage() {
			let identitiesArray = StorageUtil.read('cloudIdentities'),
					identity,
					i;

			if (identitiesArray !== null) {
				for (i = 0; i < identitiesArray.length; i++) {
					try {
						identity = CloudIdentityFactory.createFromObject(identitiesArray[i]);

						_identities[identity.id()] = identity;
					} catch (error) {
						StorageUtil.remove('cloudIdentities')
						log('readFromLocalStorage', Strings.invalid_local_data, error);
					}
				}
			}
		}

		function saveToLocalStorage() {
			let identitiesArray = new Array(),
					key;

			for (key in _identities) {
				if (_identities.hasOwnProperty(key)) {
					identitiesArray.push(_identities[key].toObject());
				}
			}

			StorageUtil.store('cloudIdentities', identitiesArray);
		}

		function parseSessionId(url) {
			let parameters = Util.parseParameters(url, true);

			if (parameters.hasOwnProperty('session')) {
				return parameters['session'];
			}

			return null;
		}

		function getCloudIdentityTransferURL(session) {
			if (typeof session === 'undefined' || session === null) {
				session = '';
			}

			return Configuration.get('CLOUD_IDENTITY_MANAGEMENT_URL') + 'collect/' + session;
		}

		function handleActivateError(message, jqXHR) {
			let error = null,
					responseJSON,
					code,
					details,
					retryCounter;

			switch (jqXHR.status) {
				case 403:
					try {
						responseJSON = JSON.parse(jqXHR.responseText);
						log('handleActivateError', message, responseJSON);
						if (responseJSON.hasOwnProperty('Code')) {
							code = responseJSON.Code;
							details = responseJSON.hasOwnProperty('Details') ? responseJSON.Details : {};
							switch (responseJSON.Code) {
								case 'urn:de:skidentity:error:client:auth:credential-revoked':
									error = new Error(ErrorType.RECOVERABLE, message, Strings.cloud_identity_revoked);
									error.identityState = 'revoked';
									break;
								case 'urn:de:skidentity:error:client:auth:credential-expired':
									error = new Error(ErrorType.RECOVERABLE, message, Strings.cloud_identity_expired);
									error.identityState = 'expired';
									break;
								case 'urn:de:skidentity:error:client:auth:invalid-binding':
								case 'urn:de:skidentity:error:client:auth:invalid-password':
									if (details.hasOwnProperty('RetryCounter')) {
										retryCounter = parseInt(details.RetryCounter);
										if (retryCounter > 0) {
											error = new Error(ErrorType.RECOVERABLE, message,
													Strings.secret_was_wrong_remaining_attempts + ': ' + retryCounter);
											error.identityState = 'good';
										} else {
											error = new Error(ErrorType.RECOVERABLE, message, Strings.secret_was_wrong_and_cloud_identity_revoked);
											error.identityState = 'revoked';
										}
									} else {
										error = new Error(ErrorType.RECOVERABLE, message, Strings.secret_was_wrong);
										error.identityState = 'good';
									}
									break;
								default:
									error = new Error(ErrorType.RECOVERABLE, message, Strings.cloud_identity_invalid);
									error.identityState = 'unknown';
							}
						}
					} catch (e) {
						// ignore
					}
					break;
				case 404:
					error = new Error(ErrorType.RECOVERABLE, message, Strings.session_expired);
					error.identityState = 'unknown';
					break;
			}

			if (error === null) {
				error = new Error(ErrorType.NON_RECOVERABLE, message, Util.toString(jqXHR));
			}

			return error;
		}

		function getCloudIDAuthenticationOption(authenticationOptions) {
			for (let key in authenticationOptions) {
				if (!authenticationOptions.hasOwnProperty(key)) {
					continue;
				}
				if (authenticationOptions[key].cloudID()) {
					return authenticationOptions[key];
				}
			}
			return null;
		}

		// public methods

		this.init = function () {
			readFromLocalStorage();
		};

		this.identities = function () {
			let identities = new Array(),
					key;

			for (key in _identities) {
				if (_identities.hasOwnProperty(key)) {
					identities.push(_identities[key]);
				}
			}

			return identities;
		};

		this.authenticationOptions = function () {
			return _authenticationOptions;
		};

		this.providerInfo = function () {
			return _providerInfo;
		};

		this.requestedAttributes = function () {
			return _requestedAttributes;
		};

		/**
		 * Check if the given CloudIdentity is usable.
		 * 
		 * Usable is defined as follows:
		 * If the optional option (or group) is given, then the given CloudIdentity is usable if it matches the
		 * given option. Otherwise, the given CloudIdentity is usable if it matches any option in this manager.
		 * 
		 * @param {CloudIdentity} identity CloudIdentity object.
		 * @param {AuthenticationOption|AuthenticationOptionGroup|undefined|null} optionalAuthenticationOption
		 * @returns {Boolean} true if check succeeds; false otherwise.
		 */
		this.isUsableIdentityByAuthenticationOption = function (identity, optionalAuthenticationOption) {
			if (typeof optionalAuthenticationOption === 'undefined' || optionalAuthenticationOption === null) {
				return (this.getAuthenticationOptionByIdentity(identity, true) !== null);
			} else {
				let ids = this.getIdentitiesByAuthenticationOption(optionalAuthenticationOption);

				for (let i = 0; i < ids.length; i++) {
					if (ids[i] === identity) {
						return true;
					}
				}
			}
			return false;
		};

		/**
		 * Get the first CloudIdentity that matches the given AuthenticationOption or AuthenticationOptionGroup.
		 * TODO: define "matches"
		 * 
		 * @param {AuthenticationOption|AuthenticationOptionGroup} authenticationOption CloudIdentity object.
		 * @returns {CloudIdentity|null}
		 */
		this.getIdentitiesByAuthenticationOption = function (authenticationOption, optionManager) {
			let identities = new Array(),
					authenticationOptions,
					key,
					i;

			if (authenticationOption instanceof AuthenticationOption) {
				authenticationOptions = [authenticationOption];
			} else if (authenticationOption instanceof AuthenticationOptionGroup) {
				authenticationOptions = authenticationOption.authenticationOptions();
			} else {
				return null;
			}
			for (key in _identities) {
				if (_identities.hasOwnProperty(key)) {
					for (i = 0; i < authenticationOptions.length; i++) {
						if (authenticationOptions[i].matchesIdentity(_identities[key], optionManager)) {
							identities.push(_identities[key]);
							// break out of inner loop over authenticationOptions and continue with next identity
							break;
						}
					}
				}
			}
			return identities;
		};

		/**
		 * Get the first AuthenticationOption (or AuthenticationOptionGroup if resolveAuthenticationOption is false)
		 * that matches the given CloudIdentity.
		 * TODO: define "matches"
		 * 
		 * @param {CloudIdentity} identity CloudIdentity object.
		 * @param {Boolean} resolveAuthenticationOption true if groups should be resolved to options.
		 * @returns {AuthenticationOption|AuthenticationOptionGroup|null}
		 */
		this.getAuthenticationOptionByIdentity = function (identity, resolveAuthenticationOption) {
			let authenticationOptions,
					i, j;

			if (!(identity instanceof CloudIdentity)) {
				return null;
			}

			if (_authenticationOptions !== null) {
				for (i = 0; i < _authenticationOptions.length; i++) {
					if (resolveAuthenticationOption && _authenticationOptions[i] instanceof AuthenticationOptionGroup) {
						authenticationOptions = _authenticationOptions[i].authenticationOptions();

						for (j = 0; j < authenticationOptions.length; j++) {
							if (authenticationOptions[j].matchesIdentity(identity)) {
								return authenticationOptions[j];
							}
						}
					} else {
						if (_authenticationOptions[i].matchesIdentity(identity)) {
							return _authenticationOptions[i];
						}
					}
				}
			}

			return null;
		};

		//Generata AuthOp for UI
		this.getAuthenticationOptionsUiByIdentities = function (identities) {
			let identity,
					authOps = Array();

			if (Util.isArray(identities)) {
				identity = identities;
			} else {
				identity = [identities];
			}

			if (!(identity[0] instanceof CloudIdentity)) {
				return [];
			}
			for (let key in identity) {
				if (_authenticationOptionsUi.hasOwnProperty(identity[key].id())) {
					authOps.push(_authenticationOptionsUi[identity[key].id()]);
				} else {
					_authenticationOptionsUi[identity[key].id()] = new AuthenticationOption("id",
							"Browser-XHR",
							identity[key].idType(),
							identity[key].idType(),
							null,
							OptionStrings.cloud_identity,
							//FIXME: don't use hardcoded url, set in util/configuration
							new Image("https://sp.skidentity.de/management-backend/api/pub/v2/assets/ID-Button_200px.png",
									200, 200),
							null, null);
					authOps.push(_authenticationOptionsUi[identity[key].id()]);
				}
			}
			return authOps;
		};

		//Get ID by previous generated AuthOp
		this.getIdentityByAuthenticationOptionUi = function (authenticationOption) {
			for (let key in _authenticationOptionsUi) {
				if (_authenticationOptionsUi[key] === authenticationOption) {
					return _identities[key];
				}
			}

			return null;
		};

		this.getAuthenticationOptionByAuthenticationOption = function (authenticationOption) {
			let authenticationOptions,
					i, j;

			for (i = 0; i < _authenticationOptions.length; i++) {
				if (_authenticationOptions[i] instanceof AuthenticationOptionGroup) {
					authenticationOptions = _authenticationOptions[i].authenticationOptions();

					for (j = 0; j < authenticationOptions.length; j++) {
						// return _authenticationOptions[i] if all its contained options match
						if (!authenticationOption.matchesAuthenticationOption(authenticationOptions[j])) {
							break;
						} else if (j === (authenticationOptions.length - 1)) {
							return _authenticationOptions[i];
						}
					}
				} else {
					if (authenticationOption.matchesAuthenticationOption(_authenticationOptions[i])) {
						return _authenticationOptions[i];
					}
				}
			}

			return null;
		};

		this.getUsableAuthenticationOptions = function () {
			let usableAuthenticationOptions = new Array(),
					i;

			for (i = 0; i < _authenticationOptions.length; i++) {
				if (this.getIdentitiesByAuthenticationOption(_authenticationOptions[i]).length === 0) {
					usableAuthenticationOptions.push(_authenticationOptions[i]);
				}
			}

			return usableAuthenticationOptions;
		};

		this.getCloudIdentityTransferURL = function (session) {
			return getCloudIdentityTransferURL(session);
		};

		/**
		 * Save the given CloudIdentity into localStorage.
		 * Removes all existing CloudIdentities from localStorage.
		 * TODO: needs to be changed for multi cloudid case.
		 * 
		 * @param {CloudIdentity} identity CloudIdentity object to save into localStorage.
		 * @returns {undefined}
		 */
		this.addCloudIdentity = function (identity) {
			if (identity === null) {
				return;
			}
			if (!_identities.hasOwnProperty(identity.id())) {
				// delete all identities (TODO: change for multi cloudid use case)
				_identities = {};
				// add identity
				_identities[identity.id()] = identity;

				saveToLocalStorage();
			}
		};

		this.getCloudIdentityOptions = function (callbacks, errorCallbacks) {
			let _name = 'getCloudIdentityOptions',
					optionsURL = Configuration.get('CLOUD_IDENTITY_MANAGEMENT_URL') + 'options';

			callLog(_name, Strings.get_cloud_identity_options_started, optionsURL, 'GET_CLOUD_IDENTITY_OPTIONS_STARTED', function () {
				jQuery.ajax({
					url: optionsURL,
					type: 'GET',
					dataType: 'xml',
					cache: false,
					success: function (options) {
						try {
							options = OptionsUtil.parseOptions(options);
							_providerInfo = options['providerInfo'];
							_authenticationOptions = options['authenticationOptions'];
							_requestedAttributes = options['requestedAttributes'];
						} catch (error) {
							error = new Error(ErrorType.NON_RECOVERABLE, Strings.get_cloud_identity_options_failed, error.message);
							callLog(_name, Strings.get_cloud_identity_options_failed, error, 'GET_CLOUD_IDENTITY_OPTIONS_FAILED', errorCallbacks);
							return;
						}

						OptionsUtil.loadImages(options, function () {
							callLog(_name, Strings.get_cloud_identity_options_succeeded, options, 'GET_CLOUD_IDENTITY_OPTIONS_SUCCEEDED', callbacks);
						}, function (error) {
							callLog(_name, Strings.get_cloud_identity_options_failed, error, 'GET_CLOUD_IDENTITY_OPTIONS_FAILED', errorCallbacks);
						});
					},
					error: function (jqXHR) {
						let error = new Error(ErrorType.NON_RECOVERABLE, Strings.get_cloud_identity_options_failed, Util.toString(jqXHR));

						callLog(_name, Strings.get_cloud_identity_options_failed, error, 'GET_CLOUD_IDENTITY_OPTIONS_FAILED', errorCallbacks);
					}
				});
			});
		};

		/**
		 * Create a new authentication flow with the CloudID FS (a.k.a. CIP) by authenticating with the given CloudIdentity.
		 * If successfull, the callbacks are called with the ID of the created authentication flow and the fsFinishUrl.
		 * 
		 * @param {type} identity
		 * @param {type} binding
		 * @param {type} callbacks
		 * @param {type} errorCallbacks
		 * @returns {undefined}
		 */
		this.createCipSession = function (cipCmd, identity, binding, secret, callbacks, errorCallbacks) {
			let _name = 'createCipSession',
				that = this;

			callLog(_name, Strings.get_cloud_identity_session_started, null, 'GET_CLOUD_IDENTITY_SESSION_STARTED', function () {
				// create AuthRequest to send to CloudID Producer
				let authRequest = {
					CIPCmd: cipCmd
				};
				that.cloudIdentityRequest(authRequest, null, function (brokerToken) {
					SessionManager.claimSession(brokerToken, function(authFlowId) {
						_optionsManager = new OptionsManager(authFlowId);
						_optionsManager.getOptions(function (options) {
							let authenticationOption = getCloudIDAuthenticationOption(options.authenticationOptions);
	
							if (authenticationOption === null) {
								// no cloudid option found
								let error = new Error(ErrorType.NON_RECOVERABLE, Strings.cloud_identity_request_failed, Strings.bad_cloud_identity_options);
								callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
								return;
							}
	
							// select cloudid option
							let requestedAttributes = authenticationOption.requestedAttributes();
							_optionsManager.selectOption(authenticationOption, requestedAttributes, function (activationURL) {
								// activate cloudid backend
								that.activateCloudIdentity(identity, binding, secret, activationURL, function (redirectURL) {
									// we ignore the redirect url to the IdS, because we're already in the IdS and just finish the auth flow at the broker
									_optionsManager.finish(function (brokerFinishResponse) {
										let response = {
											"fsUrl" : brokerFinishResponse["fsUrl"],
											"fsProtocol" : brokerFinishResponse["fsProtocol"],
											"authFlowId" : authFlowId
										}
										callLog(_name, Strings.get_cloud_identity_session_succeeded, response, 'GET_CLOUD_IDENTITY_SESSION_SUCCEEDED', callbacks);
									}, function (error) {
										callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
									});
								}, function (error) {
									callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
								});
							}, function (error) {
								callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
							});
						}, function (error) {
							callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
						});
					});
				}, function (error) {
					callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
				});
			});
		};

		this.cloudIdentityRequest = function (authRequest, relayState, callbacks, errorCallbacks) {
			let _name = 'cloudIdentityRequest',
					url = Configuration.get('CLOUD_IDENTITY_REQUEST_URL') + "auth";

			if (relayState !== null) {
				url = url + "?relay-state=" + encodeURIComponent(relayState);
			}

			callLog(_name, Strings.cloud_identity_request_started, authRequest, 'CLOUD_IDENTITY_REQUEST_STARTED', function () {
				jQuery.ajax({
					url: url,
					type: 'POST',
					contentType: 'application/json; charset=UTF-8',
					data: JSON.stringify(authRequest),
					dataType: 'text',
					success: function (brokerToken) {
						callLog(_name, Strings.cloud_identity_request_succeeded, brokerToken, 'CLOUD_IDENTITY_REQUEST_SUCCEEDED', callbacks);
					},
					error: function (jqXHR) {
						let error = new Error(ErrorType.NON_RECOVERABLE, Strings.cloud_identity_request_failed, Util.toString(jqXHR));

						callLog(_name, Strings.cloud_identity_request_failed, error, 'CLOUD_IDENTITY_REQUEST_FAILED', errorCallbacks);
					}
				});
			});
		};

		/**
		 * Issue a new CloudID for the given authentication flow.
		 * 
		 * If the request succeeded, the callbacks are called with an object of the form
		 * {session:string, identity:CloudIdentity} where session is the ID of a valid CIP session
		 * and identity is the CloudIdentity object.
		 * If given, the new cloudid is protected with newSecret.
		 * 
		 * Precondition: cipSession must be a non-empty string or
		 * identity must be a valid object of type CloudIdentity. (Note: Both may be set.)
		 */
		this.issueCloudIdentity = function (authFlowId, identity, binding, secret, newBinding, callbacks, errorCallbacks, remote) {
			let that = this,
				url,
				_name = "issueCloudIdentity";


			if (! Util.isValidString(authFlowId)) {
				// no CIP session given, so create one first and re-call getCloudIdentity with that session
				this.createCipSession('issue', identity, binding, secret, function (brokerFinishResp) {
					that.issueCloudIdentity(brokerFinishResp["authFlowId"], null, null, secret, newBinding, callbacks, errorCallbacks);
				}, function (error) {
					callLog(_name, Strings.issue_cloud_identity_failed, error, 'ISSUE_CLOUD_IDENTITY_FAILED', errorCallbacks);
				});
			} else {
				url = Configuration.get('CLOUD_IDENTITY_REQUEST_URL') + "issue?flow-id=" + authFlowId;

				let issue = function (data) {
					callLog(_name, Strings.issue_cloud_identity_started, url, 'ISSUE_CLOUD_IDENTITY_STARTED', function () {
						jQuery.ajax({
							url: url,
							type: 'POST',
							contentType: 'application/x-www-form-urlencoded',
							dataType: 'text',
							data: data,
							success: function (response) {
								let identity, result;
								
								//Send raw cID string to other IdS
								if (!remote) {
									try {
										identity = CloudIdentityFactory.create(response, newBinding, newBinding.salt());
									} catch (error) {
										error = new Error(ErrorType.NON_RECOVERABLE, Strings.issue_cloud_identity_failed, error.message);
										callLog(_name, Strings.issue_cloud_identity_failed, error, 'ISSUE_CLOUD_IDENTITY_FAILED', errorCallbacks);
										return;
									}

									result = {
										session: authFlowId,
										identity: identity
									};
								} else {
									result = response;
								}
								
								callLog(_name, Strings.issue_cloud_identity_succeeded, result, 'ISSUE_CLOUD_IDENTITY_SUCCEEDED', callbacks);
							},
							error: function (jqXHR) {
								if (jqXHR.status === 404 && identity !== null) {
									log(_name, 'Authentication flow invalid or expired.', authFlowId);
									that.issueCloudIdentity(null, identity, binding, secret, newBinding, callbacks, errorCallbacks);
								} else {
									let error = new Error(ErrorType.NON_RECOVERABLE, Strings.issue_cloud_identity_failed, Util.toString(jqXHR));

									callLog(_name, Strings.issue_cloud_identity_failed, error, 'ISSUE_CLOUD_IDENTITY_FAILED', errorCallbacks);
								}
							}
						});
					});
				};

				let issuePassword = function (params) {
					let data = new Object();

					if (params['key'] !== null) {
						data['bind-type'] = 'http://ws.skidentity.de/ci/binding/password/v1.0';
						data['bind-data'] = params['key'];
					}

					issue(data);
				};

				let issueWebCryptoKey = function (keyBinding) {
					let key = webcrypto.loadKey(keyBinding.keyId(), keyBinding.secret());
					let nonce = Util.randomString(20);
					let pvr = webcrypto.hashHex(keyBinding.salt() + secret, webcrypto.hashAlgo.sha256);
					let toHash = pvr + nonce + authFlowId;
					
					let jws = key.signAndReturnJWS(webcrypto.hashRaw(toHash, webcrypto.hashAlgo.sha256), webcrypto.hashAlgo.sha256);
					
					let data = new Object();
					let bindData = new Object();

					bindData.sty = "JWS";
					bindData.sig = jws;
					bindData.aux = {};
					bindData.aux.pha = webcrypto.hashAlgo.sha256;
					bindData.pvr = pvr;

					data['bind-type'] = 'http://ws.skidentity.de/ci/binding/key/v1.0';
					data['bind-data'] = JSON.stringify(bindData);
					data['nonce'] = nonce;

					issue(data);
				};
				
				let issueFidoWebAuthn = function (webAuthnBinding) {
					let data = new Object();
					let bindData = new Object();
				
					bindData.sty = "WebAuthn";
					bindData.sig = Base64Util.encodeBase64Url(webAuthnBinding.attestation());
					bindData.aux = {};
					bindData.aux.typ = "Registration";
					bindData.aux.aid = webAuthnBinding.appId() ? webAuthnBinding.appId() : window.location.host;
					bindData.aux.cld = Base64Util.encodeBase64Url(webAuthnBinding.clientData());
					bindData.pvr = webAuthnBinding.pvr();
		
					data['bind-type'] = 'http://ws.skidentity.de/ci/binding/key/v1.0';
					data['bind-data'] = JSON.stringify(bindData);
					data['nonce'] = webAuthnBinding.nonce();

					issue(data);
				};

				if (remote) {
					issue(newBinding);
				} else {
					switch (newBinding.type()) {
						case "http://ws.skidentity.de/ci/binding/password/v1.0":
							CryptoUtil.deriveKey(newBinding.secret(), newBinding.salt(), issuePassword);
							break;
						case "http://ws.skidentity.de/ci/binding/key/v1.0":
							switch(newBinding.origin()) {
								case "WebCrypto":
									issueWebCryptoKey(newBinding);
									break;
								case "FIDO/WebAuthn":
									issueFidoWebAuthn(newBinding);
									break;
							}
							break;
					}
				}
			}
		};

		/**
		 * Process the given CIP response to issue a new CloudIdentity.
		 * 
		 * @param {Object} authFlowId identifies the current authentication flow
		 * @param {Array|function|undefined} callbacks
		 * @param {Array|function|undefined} errorCallbacks
		 * @returns {undefined}
		 */
		this.getCloudIdentity = function (authFlowId, binding, secret, callbacks, errorCallbacks, remote) {
			let _name = 'getCloudIdentity',
				that = this;

			callLog(_name, Strings.get_cloud_identity_started, authFlowId, 'GET_CLOUD_IDENTITY_STARTED', function () {
				let newBinding = binding;
				remote = remote ? remote : false;

				//getCloudId is used only once -> redirect with cip-session
				that.issueCloudIdentity(authFlowId, null, null, secret, newBinding, function (result) {
					// sucess
					callLog(_name, Strings.get_cloud_identity_succeeded, result, 'GET_CLOUD_IDENTITY_SUCCEEDED', callbacks);
				}, function(error) {
					// error
					callLog(_name, Strings.get_cloud_identity_failed, error, 'GET_CLOUD_IDENTITY_FAILED', errorCallbacks);
				}, remote);
			});
		};

		this.revokeCloudIdentities = function (authFlowId, callbacks, errorCallbacks) {
			let _name = 'revokeCloudIdentities';

			callLog(_name, Strings.revoke_cloud_identities_started, authFlowId, 'REVOKE_CLOUD_IDENTITIES_STARTED', function () {
				let url;

				url = Configuration.get('CLOUD_IDENTITY_REQUEST_URL') + "revoke-all?flow-id=" + authFlowId;
				jQuery.ajax({
					url: url,
					type: 'POST',
					contentType: 'application/x-www-form-urlencoded',
					success: function () {
						callLog(_name, Strings.revoke_cloud_identities_succeeded, null, 'REVOKE_CLOUD_IDENTITIES_SUCCEEDED', callbacks);
					},
					error: function (jqXHR) {
						let error = new Error(ErrorType.NON_RECOVERABLE, Strings.revoke_cloud_identities_failed, Util.toString(jqXHR));
						callLog(_name, Strings.revoke_cloud_identities_failed, error, 'REVOKE_CLOUD_IDENTITIES_FAILED', errorCallbacks);
					}
				});
			});
		};

		this.activateCloudIdentity = function (identity, binding, secret, activationURL, callbacks, errorCallbacks) {
			let _name = 'activateCloudIdentity';

			let activate = function(data) {
				callLog(_name, Strings.activate_cloud_identity_started,
						{identity: identity, activationURL: activationURL}, 'ACTIVATE_CLOUD_IDENTITY_STARTED', function () {
					jQuery.ajax({
						url: activationURL,
						type: 'POST',
						contentType: 'application/x-www-form-urlencoded',
						dataType: 'text',
						data: data,
						success: function (redirectURL) {
							let error;

							if (!Util.isValidURL(redirectURL)) {
								error = new Error(ErrorType.NON_RECOVERABLE, Strings.activate_cloud_identity_failed, Strings.invalid_redirect_url + redirectURL);
								callLog(_name, Strings.activate_cloud_identity_failed, error, 'ACTIVATE_CLOUD_IDENTITY_FAILED', errorCallbacks);
								return;
							}

							callLog(_name, Strings.activate_cloud_identity_succeeded, redirectURL, 'ACTIVATE_CLOUD_IDENTITY_SUCCEEDED', callbacks);
						},
						error: function (jqXHR) {
							let error = handleActivateError(Strings.activate_cloud_identity_failed, jqXHR);

							callLog(_name, Strings.activate_cloud_identity_failed, error, 'ACTIVATE_CLOUD_IDENTITY_FAILED', errorCallbacks);
						}
					});
				});
			};

			let activatePassword = function (params) {
				let data = {
					cloudid: identity.cloudIdentity()
				};
				if (params['key'] !== null) {
					data['bind-type'] = 'http://ws.skidentity.de/ci/binding/password/v1.0';
					data['bind-data'] = Sha1.hash(params['key'] + ':' + parseSessionId(activationURL));
				}
				activate(data);
			};
			
			let activateWebCryptoKey = function (keyBinding) {
				let key = webcrypto.loadKey(keyBinding.keyId(), secret);
				let nonce = Util.randomString(20);
				let pvr = keyBinding.getPVR();
				
				let toHash = pvr + nonce + parseSessionId(activationURL);
				let jws = key.signAndReturnJWS(webcrypto.hashRaw(toHash, webcrypto.hashAlgo.sha256), webcrypto.hashAlgo.sha256);

				let data = {
					cloudid: identity.cloudIdentity()
				};
				let bindData = new Object();

				bindData.sty = "JWS";
				bindData.sig = jws;
				bindData.aux = {};
				bindData.aux.pha = webcrypto.hashAlgo.sha256;
				bindData.pvr = pvr;

				data['bind-type'] = 'http://ws.skidentity.de/ci/binding/key/v1.0';
				data['bind-data'] = JSON.stringify(bindData);
				data['nonce'] = nonce;

				activate(data);
			};
			
			let activateFidoWebAuthn = function (webAuthnBinding) {
				let nonce = Util.randomString(20);
				let pvr = webAuthnBinding.getPVR();
				
				let toHash = pvr + nonce + parseSessionId(activationURL);
				let challenge = Base64Util.decodeBase64(webcrypto.hashBase64(toHash, webcrypto.hashAlgo.sha256));
						
				let appId = webAuthnBinding.appId() ? webAuthnBinding.appId() : window.location.host;
				let credentialId = webAuthnBinding.credentialId();

				let publicKey = {
					"rpId": appId,
					"challenge": challenge,
					"allowCredentials": [{ "type": "public-key", "id": credentialId }]
				}

				setTimeout(function(){
					navigator.credentials.get({ "publicKey": publicKey })
						.then(function (assertion) {
						// Send assertion to server for verification
						let bindData = new Object();

						let data = { cloudid: identity.cloudIdentity() };
						let signatureBase64Url = Base64Util.encodeBase64Url(assertion.response.signature);
						let clientDataBase64Url = Base64Util.encodeBase64Url(assertion.response.clientDataJSON);
						let authenticatorDataBase64Url = Base64Util.encodeBase64Url(assertion.response.authenticatorData);

						bindData.sty = "WebAuthn";
						bindData.sig = signatureBase64Url;
						bindData.aux = {};
						bindData.aux.typ = "Authentication";
						bindData.aux.aid = appId;
						bindData.aux.cld = clientDataBase64Url;
						bindData.aux.ad = authenticatorDataBase64Url;
						bindData.aux.pha = webcrypto.hashAlgo.sha256;
						bindData.pvr = pvr;

						data['bind-type'] = 'http://ws.skidentity.de/ci/binding/key/v1.0';
						data['bind-data'] = JSON.stringify(bindData);
						data['nonce'] = nonce;

						activate(data);
					}).catch(function (err) {
						// No acceptable credential or user refused consent. Handle appropriately.
						let error = new Error(ErrorType.RECOVERABLE, Strings.activate_cloud_identity_failed, Strings.activate_cloud_identity_failed);
						callLog(_name, Strings.activate_cloud_identity_failed, error, 'ACTIVATE_CLOUD_IDENTITY_FAILED', errorCallbacks);
					});
				}, 200);
			};
			
			switch (binding.type()) {
				case "http://ws.skidentity.de/ci/binding/password/v1.0":
					CryptoUtil.deriveKey(binding.secret(), binding.salt(), activatePassword);
					break;
				case "http://ws.skidentity.de/ci/binding/key/v1.0":
					switch(binding.origin()) {
						case "WebCrypto":
							activateWebCryptoKey(binding);
							break;
						case "FIDO/WebAuthn":
							activateFidoWebAuthn(binding);
							break;
					}
					break;
			}
		};

		/**
		 * Get attributes for given CIP session. If CIP session is null or invalid (404), then new session
		 * is created using given identity (if not null) and secret.
		 * 
		 * If the request succeeded, the callbacks are called with an object of the form
		 * {session:string, attributes:Attribute*} where session is the ID of a valid CIP session
		 * and attributes is an array of Attribute objects.
		 * 
		 * Precondition: authFlowId must be a non-empty string or
		 * identity must be a valid object of type CloudIdentity. (Note: Both may be set.)
		 */
		this.getCloudIdentityAttributes = function (authFlowId, identity, binding, secret, callbacks, errorCallbacks) {
			let _name = 'getCloudIdentityAttributes',
				that = this,
				url;

			if (! Util.isValidString(authFlowId)) {
				this.createCipSession('activation', identity, binding, secret, function (brokerFinishResp) {
					that.getCloudIdentityAttributes(brokerFinishResp["authFlowId"], null, null, secret, callbacks, errorCallbacks);
				}, function (error) {
					callLog(_name, Strings.get_cloud_identity_attributes_failed, error, 'GET_CLOUD_IDENTITY_ATTRIBUTES_FAILED', errorCallbacks);
				});
			} else {
				// try to use given CIP session, only re-create session if session is invalid (404)
				url = Configuration.get('CLOUD_IDENTITY_REQUEST_URL') + "attributes?flow-id=" + authFlowId;
				callLog(_name, Strings.get_cloud_identity_attributes_started, url, 'GET_CLOUD_IDENTITY_ATTRIBUTES_STARTED', function () {
					jQuery.ajax({
						url: url,
						type: 'GET',
						dataType: 'xml',
						success: function (cloudIdentityXML) {
							let attributes;

							try {
								attributes = AttributesFactory.create(cloudIdentityXML);
							} catch (error) {
								error = new Error(ErrorType.NON_RECOVERABLE, Strings.get_cloud_identity_attributes_failed, error.message);
								log(_name, Strings.get_cloud_identity_attributes_failed, error);
								Util.callCallbacks(errorCallbacks, error);
								return;
							}

							let result = {
								authFlowId: authFlowId,
								attributes: attributes
							};
							callLog(_name, Strings.get_cloud_identity_attributes_succeeded, result, 'GET_CLOUD_IDENTITY_ATTRIBUTES_SUCCEEDED', callbacks);
						},
						error: function (jqXHR) {
							if (jqXHR.status === 404 && identity !== null) {
								log(_name, 'Authentication Flow invalid or expired.', authFlowId);
								that.getCloudIdentityAttributes(null, identity, binding, secret, callbacks, errorCallbacks);
							} else {
								let error = new Error(ErrorType.NON_RECOVERABLE, Strings.get_cloud_identity_attributes_failed, Util.toString(jqXHR));
								callLog(_name, Strings.get_cloud_identity_attributes_failed, error, 'GET_CLOUD_IDENTITY_ATTRIBUTES_FAILED', errorCallbacks);
							}
						}
					});
				});
			}
		};
		
		/**
		 * Create OptionManager to check if PVR is used for getCloudIdentityAttributes
		 * 
		 * @param {type} callbacks
		 * @param {type} errorCallbacks
		 * @returns {undefined}
		 */
		this.usePVRForGetCloudIdentityAttributes = function (callbacks, errorCallbacks) {
			let _name = 'usePVRForGetCloudIdentityAttributes',
					that = this;

			callLog(_name, Strings.get_cloud_identity_session_started, null, 'GET_CLOUD_IDENTITY_SESSION_STARTED', function () {
				// create AuthRequest to send to CloudID Producer
				let authRequest = {
					CIPCmd: 'activation'
				};
				that.cloudIdentityRequest(authRequest, null, function (brokerSession) {
					SessionManager.claimSession(brokerSession, function(authFlowId) {
						_optionsManager = new OptionsManager(authFlowId);
						_optionsManager.getOptions(function (options) {
							let authenticationOption = getCloudIDAuthenticationOption(options.authenticationOptions);
	
							if (authenticationOption === null) {
								// no cloudid option found
								let error = new Error(ErrorType.NON_RECOVERABLE, Strings.cloud_identity_request_failed, Strings.bad_cloud_identity_options);
								callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
								return;
							}
							callbacks(_optionsManager.usePVRforOption(authenticationOption));
						}, function (error) {
							callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
						});
					});
				}, function (error) {
					callLog(_name, Strings.get_cloud_identity_session_failed, error, 'GET_CLOUD_IDENTITY_SESSION_FAILED', errorCallbacks);
				});
			});
		};

		this.enableCloudIdentity = function (identity, callbacks) {
			log('enableCloudIdentity', Strings.enabled_cloud_identity, identity.id());

			identity.enable();
			saveToLocalStorage();

			Util.callCallbacks(callbacks);
		};

		this.disableCloudIdentity = function (identity, callbacks) {
			log('disableCloudIdentity', Strings.disabled_cloud_identity, identity.id());

			identity.disable();
			saveToLocalStorage();

			Util.callCallbacks(callbacks);
		};

		/**
		 * Create a transfer URL (a.k.a. collect URL) for a CloudID created from the given CIP session
		 * and protected with newSecret and newSalt.
		 * If CIP session is null or invalid (404), then new session
		 * is created using given identity (if not null) and secret.
		 * 
		 * If the request succeeded, the callbacks are called with an object of the form
		 * {session:string, url:string} where session is the ID of a valid CIP session
		 * and url is the transfer URL (or collect URL).
		 * 
		 * Precondition: authFlowId must be a non-empty string or
		 * identity must be a valid object of type CloudIdentity. (Note: Both may be set.)
		 */
		//TODO: binding
		this.transferCloudIdentity = function (authFlowId, identity, secret, newSecret, newSalt, callbacks, errorCallbacks) {
			let _name = 'transferCloudIdentity',
				that = this,
				url;

			if (! Util.isValidString(authFlowId)) {
				this.createCipSession('issue', identity, binding, secret, function (brokerFinishResp) {
					that.transferCloudIdentity(brokerFinishResp["authFlowId"], null, null, newSecret, newSalt, callbacks, errorCallbacks);
				}, function (error) {
					callLog(_name, Strings.transfer_cloud_identity_started, error, 'TRANSFER_CLOUD_IDENTITY_FAILED', errorCallbacks);
				});
			} else {
				// try to use given CIP session, only re-create session if session is invalid (404)
				url = Configuration.get('CLOUD_IDENTITY_REQUEST_URL') + "transfer?flow-id=" + authFlowId;
				callLog(_name, Strings.transfer_cloud_identity_started, url, 'TRANSFER_CLOUD_IDENTITY_STARTED', function () {

					CryptoUtil.deriveKey(newSecret, newSalt, function (params) {
						let data = new Object();
						if (params['key'] !== null) {
							data['bind-type'] = 'http://ws.skidentity.de/ci/binding/password/v1.0';
							data['bind-data'] = params['key'];
						}

						jQuery.ajax({
							url: url,
							type: 'POST',
							contentType: 'application/x-www-form-urlencoded',
							data: data,
							dataType: 'text',
							success: function (transferURL) {
								let idmTransferURL,
									error;

								if (!Util.isValidURL(transferURL)) {
									error = new Error(ErrorType.NON_RECOVERABLE, Strings.transfer_cloud_identity_failed,
											Strings.invalid_transfer_url + transferURL);
									callLog(_name, Strings.transfer_cloud_identity_failed, error, 'TRANSFER_CLOUD_IDENTITY_FAILED', errorCallbacks);
									return;
								}

								if (transferURL.indexOf(getCloudIdentityTransferURL()) === 0) {
									idmTransferURL = Util.currentURL() + '#ctx=idm&trans=' +
											transferURL.substring(getCloudIdentityTransferURL().length, transferURL.length) +
											'&salt=' + newSalt;
								} else {
									idmTransferURL = Util.currentURL() + '#ctx=idm&trans=' + encodeURIComponent(transferURL) +
											'&salt=' + newSalt;
								}

								let result = {
									authFlowId: authFlowId,
									url: idmTransferURL
								};
								callLog(_name, Strings.transfer_cloud_identity_succeeded, result, 'TRANSFER_CLOUD_IDENTITY_SUCCEEDED', callbacks);
							},
							error: function (jqXHR) {
								if (jqXHR.status === 404 && identity !== null) {
									log(_name, 'Auth Flow invalid', authFlowId);
									that.transferCloudIdentity(null, identity, secret, newSecret, newSalt, callbacks, errorCallbacks);
								} else {
									let error = new Error(ErrorType.NON_RECOVERABLE, Strings.transfer_cloud_identity_failed, Util.toString(jqXHR));
									callLog(_name, Strings.transfer_cloud_identity_failed, error, 'TRANSFER_CLOUD_IDENTITY_FAILED', errorCallbacks);
								}
							}
						});
					});
				});
			}
		};

		this.collectCloudIdentity = function (collectUrl, salt, callbacks, errorCallbacks) {
			let _name = 'collectCloudIdentity';

			callLog(_name, Strings.collect_cloud_identity_started, collectUrl, 'COLLECT_CLOUD_IDENTITY_STARTED', function () {
				jQuery.ajax({
					url: collectUrl,
					type: 'POST',
					contentType: 'application/x-www-form-urlencoded',
					dataType: 'text',
					success: function (response) {
						let identity;

						try {
							//TODO: bindings
							identity = CloudIdentityFactory.create(response, salt);
						} catch (error) {
							error = new Error(ErrorType.NON_RECOVERABLE, Strings.collect_cloud_identity_failed, error.message);
							callLog(_name, Strings.collect_cloud_identity_failed, error, 'COLLECT_CLOUD_IDENTITY_FAILED', errorCallbacks);
							return;
						}

						callLog(_name, Strings.collect_cloud_identity_succeeded, identity, 'COLLECT_CLOUD_IDENTITY_SUCCEEDED', callbacks);
					},
					error: function (jqXHR) {
						let error = new Error(ErrorType.NON_RECOVERABLE, Strings.collect_cloud_identity_failed, Util.toString(jqXHR));

						callLog(_name, Strings.collect_cloud_identity_failed, error, 'COLLECT_CLOUD_IDENTITY_FAILED', errorCallbacks);
					}
				});
			});
		};

		this.deleteCloudIdentity = function (identity, revoke, callbacks/*, errorCallbacks*/) {
			let _name = 'deleteCloudIdentity';

			log(_name, Strings.delete_cloud_identity_started, identity.id());

			if (!revoke) {
				delete _identities[identity.id()];

				saveToLocalStorage();

				log(_name, Strings.delete_cloud_identity_succeeded, identity.id());
				Util.callCallbacks(callbacks);

				return;
			}

			jQuery.ajax({
				url: Configuration.get('CLOUD_IDENTITY_MANAGEMENT_URL') + 'revoke',
				type: 'POST',
				contentType: 'application/x-www-form-urlencoded',
				data: {id: identity.id()},
				success: function () {
					delete _identities[identity.id()];

					saveToLocalStorage();

					log(_name, Strings.delete_cloud_identity_succeeded, identity.id());
					Util.callCallbacks(callbacks);
				},
				error: function (jqXHR) {
					let error = new Error(ErrorType.RECOVERABLE, Strings.revoke_cloud_identity_failed, Util.toString(jqXHR));

					delete _identities[identity.id()];

					saveToLocalStorage();

					log(_name, Strings.revoke_cloud_identity_failed, error);
					log(_name, Strings.delete_cloud_identity_succeeded, identity.id());
					Util.callCallbacks(callbacks);
				}
			});
		};

		function getAllAuthenticationOptions() {
			// map groups to arrays of options
			let options = _authenticationOptions.map(function (optionOrGroup) {
				if (optionOrGroup instanceof AuthenticationOption) {
					return [optionOrGroup];
				} else {
					// optionOrGroup is instance of AuthenticationOptionGroup
					return optionOrGroup.authenticationOptions();
				}
			});
			// flatten the array of all options
			return [].concat.apply([], options);
		}

		/**
		 * Get the AuthenticationOption with UIInfo of the given type, protocol and issuer.
		 * 
		 * @param {string} type The AuthenticationOption Type.
		 * @param {string} protocol The AuthenticationOption Protocol.
		 * @param {string|null|undefined} issuer The AuthenticationOption Issuer, may be missing.
		 * @returns {AuthenticationOption|null} The AuthenticationOption object, or null if not found.
		 */
		this.getAuthenticationOption = function (type, protocol, issuer) {
			issuer = Util.isValidString(issuer) ? issuer : null;
			// search in all AuthenticationOptions
			return Util.find(getAllAuthenticationOptions(), function (option) {
				// type must match
				return option.type() === type &&
						// protocol must match
						option.protocol() === protocol &&
						// option's issuers must be empty or issuer must be in option's issuers list
								(option.issuer().length === 0 ||
										(issuer !== null &&
												-1 !== Util.findIndex(option.issuer(), function (iss) {
													return X500Util.compare(iss, issuer);
												})));
					});
		};

		/**
		 * Get the AuthenticationOption with UIInfo for the given Origin.
		 * 
		 * @param {Origin} origin The Origin that specifies an AuthenticationOption.
		 * @returns {AuthenticationOption|null} The AuthenticationOption object, or null if not found.
		 */
		this.getAuthenticationOptionForOrigin = function (origin) {
			let issuer = (origin.issuer().length === 0) ? null : origin.issuer()[0];
			return this.getAuthenticationOption(origin.type(), origin.protocol(), issuer);
		};

	};
});


//////////////////
// WEBPACK FOOTER
// ./src/main/web/js/app/service/CloudIdentitiesManager.js
// module id = 105
// module chunks = 4